import {
  GlobalPositionStrategy,
  Overlay,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, inject, Injectable, Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { combineLatest, NEVER } from 'rxjs';
import {
  auditTime,
  distinctUntilChanged,
  skip,
  switchMap,
} from 'rxjs/operators';
import { getAnimationFrameScheduler } from '../../../../../cross-team-shared/misc-utils';
import { isOrthographicVideoOutput } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { settingsStoreToken } from '../../../../../cross-team-shared/state/setting-state';
import { AbstractAnnotationStore } from '../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { EventTrackingService } from '../../../../../shared/event-tracking/event-tracking.service';
import { outputInfoStoreToken } from '../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { contentConnectorToken } from '../../utils/misc-utils';
import { InteractionModeService } from '../utils/interaction-mode.service';
import { OutputKeyboardShortcutService } from '../utils/output-keyboard-shortcut.service';
import { ZoomPanelComponent } from './zoom-panel.component';

/**
 * to prevent mouse clicks from being intercepted by the zoom panel, we
 *  added a 2 pixel padding to the position of the zoom panel.
 */
export const MINIMUM_PADDING = 2;
/**
 * The panel size of 400px is also used in the zoom-panel.component.scss so
 * these values should be kept in sync
 */
export const ZOOM_PANEL_WIDTH = 400;

export type ToggleZoomPanelMethod = 'button' | 'shortcut' | 'tool';

@Injectable()
export class ZoomPanelService extends InjectableSuperclass {
  static padding = 10;

  // This is set to -2 so that the zoom panel doesn't prevent mouse clicks
  private paddingLeft = -MINIMUM_PADDING;
  private paddingTop = -MINIMUM_PADDING;

  private zoomPanelComponent!: ComponentPortal<ZoomPanelComponent>;

  private overlayRef!: OverlayRef;
  private currentStrategy = new GlobalPositionStrategy();
  private options: Partial<ZoomPanelComponent> = {};
  private componentRef!: ComponentRef<ZoomPanelComponent>;
  private settingsStore = inject(settingsStoreToken);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private outputInfoStore = inject(outputInfoStoreToken);
  private interactionModeService = inject(InteractionModeService);
  private overlay = inject(Overlay);
  private injector = inject(Injector);
  private store = inject(AbstractAnnotationStore);
  private eventTrackingService = inject(EventTrackingService);
  private keyboardShortcutService = inject(OutputKeyboardShortcutService);
  private contentConnector = inject(contentConnectorToken);

  setUpZoomPanelSubscription(options: Partial<ZoomPanelComponent> = {}) {
    this.options = options;

    this.subscribeToCanvasMode();
    if (this.taskWorkspaceStore.isUsingObjects()) {
      this.setupSubscriptionToRootZoomPanelOpenState();
    } else {
      this.subscribeTo(
        this.keyboardShortcutService.getEvent$('toggleZoomPanel'),
        () => this.toggleZoomPanel('shortcut'),
      );
    }

    this.subscribeTo(
      this.store('zoomPanel')('isOpen').$.pipe(
        switchMap((isZoomPanelOpen) =>
          isZoomPanelOpen ? this.store('mousePosition').$ : NEVER,
        ),
        // Throttling the repositioning to the speed at which the browser can
        //  complete the animation.
        auditTime(0, getAnimationFrameScheduler()),
      ),
      this.updatePosition,
    );
  }

  toggleZoomPanel(method: ToggleZoomPanelMethod) {
    if (this.store.state().zoomPanel.isOpen) {
      this.closeZoomPanel(method);
      return;
    }
    this.openZoomPanel(true, method);
  }

  isZoomPanelOpen() {
    return this.store.state().zoomPanel.isOpen;
  }

  openZoomPanel(manuallyOpened: boolean, method?: ToggleZoomPanelMethod) {
    const output = this.outputInfoStore.state().output;
    if (
      this.store.state().zoomPanel.isOpen ||
      isOrthographicVideoOutput(output)
    ) {
      return;
    }

    this.store('zoomPanel')('manuallyOpened').set(manuallyOpened);

    this.openPanel();
    this.store('zoomPanel')('isOpen').set(true);
    this.contentConnector.setIsZoomPanelOpen(true);

    if (method) {
      this.trackEvent(method);
    }
  }

  closeZoomPanel(method?: ToggleZoomPanelMethod) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (this.componentRef) {
      this.options.isHidden = true;
      Object.assign(this.componentRef.instance, this.options);
    }
    // We no longer dispose of the canvas to avoid losing 3D context

    this.store('zoomPanel')('isOpen').set(false);
    this.contentConnector.setIsZoomPanelOpen(false);
    this.store('zoomPanel')('manuallyOpened').set(false);
    if (method) {
      this.settingsStore('zoomPanel')('isOpen').set(false);
      this.trackEvent(method);
    }
  }

  private getZoomPanelEnabledStore() {
    return this.taskWorkspaceStore.isUsingObjects()
      ? this.settingsStore('layerSettings2d')('zoomPanelEnabled')
      : this.store('zoomPanel')('enabled');
  }

  private subscribeToCanvasMode() {
    this.subscribeTo(
      this.getZoomPanelEnabledStore().$.pipe(
        switchMap((enabled) =>
          enabled ? this.interactionModeService.isInMode$('canvas') : NEVER,
        ),
        distinctUntilChanged(),
        skip(1),
      ),
      (isInCanvasMode) => {
        if (!isInCanvasMode) {
          this.closeZoomPanelIfAutomaticallyOpened();
        } else {
          this.openZoomPanel(false);
        }
      },
    );
  }

  private setupSubscriptionToRootZoomPanelOpenState() {
    if (isOrthographicVideoOutput(this.outputInfoStore.state().output)) {
      return;
    }

    this.subscribeTo(
      combineLatest([
        this.settingsStore('zoomPanel')('isOpen').$,
        this.taskWorkspaceStore('selectedOutputName').$,
      ]),
      ([isOpen, selectedOutputName]) => {
        if (
          selectedOutputName !== this.outputInfoStore.state().output.name ||
          !isOpen
        ) {
          this.closeZoomPanel();
        } else {
          this.openZoomPanel(true);
        }
      },
    );
  }

  private openPanel() {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.zoomPanelComponent) {
      this.initializeZoomPanel();
    }
    // need this because for some reason, the panel sometimes registers the
    // click and triggers the annotation layout blur which closes the zoom panel
    const panelClass = ['no-pointer-events'];
    this.currentStrategy = this.updateClingyPositionStrategy();

    this.overlayRef.updatePositionStrategy(this.currentStrategy);
    this.overlayRef.addPanelClass(panelClass);
    this.options.isHidden = false;
    Object.assign(this.componentRef.instance, this.options);
  }

  private initializeZoomPanel() {
    this.zoomPanelComponent = new ComponentPortal(
      ZoomPanelComponent,
      null,
      this.injector,
    );
    this.overlayRef = this.overlay.create({
      positionStrategy: this.currentStrategy,
      panelClass: ['no-pointer-events'],
    });
    this.componentRef = this.overlayRef.attach(this.zoomPanelComponent);
  }

  private updatePosition() {
    if (!this.getZoomPanelEnabledStore().state()) {
      return;
    }
    this.overlayRef.updatePositionStrategy(this.updateClingyPositionStrategy());
    this.overlayRef.updatePosition();
  }

  private updateClingyPositionStrategy() {
    const mousePosition = this.store.state().mousePosition;
    assert(mousePosition, 'The mouse position can not be undefined.');
    const panelWidth = ZOOM_PANEL_WIDTH;
    const left = this.getLeftPadding(mousePosition.clientPoint[0], panelWidth);
    const top = this.getTopPadding(mousePosition.clientPoint[1], panelWidth);

    return this.overlay.position().global().left(`${left}px`).top(`${top}px`);
  }

  private getLeftPadding(clientPointX: number, panelWidth: number) {
    const windowWidth = window.innerWidth;
    if (this.paddingLeft !== -MINIMUM_PADDING) {
      if (clientPointX <= panelWidth) {
        this.paddingLeft = -MINIMUM_PADDING;
      }
    } else if (clientPointX + panelWidth > windowWidth) {
      this.paddingLeft = panelWidth;
    }
    return clientPointX - this.paddingLeft;
  }

  private getTopPadding(clientPointY: number, panelheight: number) {
    const windowHeight = window.innerHeight;
    if (this.paddingTop !== -MINIMUM_PADDING) {
      if (clientPointY <= panelheight) {
        this.paddingTop = -MINIMUM_PADDING;
      }
    } else if (clientPointY + panelheight > windowHeight) {
      this.paddingTop = panelheight;
    }
    return clientPointY - this.paddingTop;
  }

  private closeZoomPanelIfAutomaticallyOpened() {
    const { isOpen, manuallyOpened } = this.store.state().zoomPanel;
    if (isOpen && !manuallyOpened) {
      this.closeZoomPanel();
    }
  }

  private trackEvent(method: ToggleZoomPanelMethod) {
    this.eventTrackingService.track({
      id: 'toggle_zoom_panel',
      bucket: 'navigation',
      sub_bucket: 'frame',
      new_value: this.store.state().zoomPanel.isOpen,
      method,
    });
  }
}
